import React from 'react';
import { FREE_TRIAL_TOTAL } from './FreeTrialBanner.jsx';

const CLINICALS_TIERS = [
    { name: 'Class Prep', price: '$9.99/mo', credits: '1,000 credits/mo', highlight: 'Smart reasoning engine', color: 'teal' },
    { name: 'Board Review', price: '$19.99/mo', credits: '2,500 credits/mo', highlight: 'Advanced reasoning engine', color: 'emerald', popular: true },
    { name: 'Specialty Boards', price: '$34.99/mo', credits: '4,000 credits/mo', highlight: 'Most powerful engine', color: 'sky' },
];

const ClinicalGateModal = ({ onUpgrade, onSignUp, onClose, isAnonymous }) => (
    <div
        role="dialog"
        aria-modal="true"
        onClick={onClose}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backdropFilter: 'blur(12px)', backgroundColor: 'rgba(7,15,30,0.85)' }}
    >
        {/* Panel */}
        <div
            onClick={e => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-3xl bg-[#0e1f2e] border border-teal-500/20 shadow-2xl shadow-teal-900/40 overflow-hidden"
        >

            {/* Teal glow top strip */}
            <div className="h-1 w-full bg-gradient-to-r from-teal-400 via-emerald-400 to-cyan-400" />

            <div className="p-6 sm:p-8">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-xs font-semibold text-emerald-400 tracking-wider uppercase">FlashFonic Clinicals</span>
                        </div>
                        <h2 className="text-2xl font-bold text-white">Free Trial Complete</h2>
                        <p className="mt-1 text-sm text-slate-400">
                            You've used all {FREE_TRIAL_TOTAL} free questions. Subscribe to keep generating — credits refresh every month.
                        </p>
                    </div>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="shrink-0 rounded-full p-1.5 text-slate-500 hover:text-slate-300 hover:bg-white/5 transition"
                            aria-label="Close"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Progress — fully used */}
                <div className="mb-6 rounded-2xl bg-slate-800/50 border border-white/5 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-slate-400">Free questions used</span>
                        <span className="text-xs font-semibold text-amber-300">{FREE_TRIAL_TOTAL}/{FREE_TRIAL_TOTAL}</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-slate-700">
                        <div className="h-full w-full rounded-full bg-gradient-to-r from-amber-400 to-orange-400" />
                    </div>
                </div>

                {/* 1 credit = 1 card note */}
                <p className="mb-4 text-center text-xs text-slate-500">1 credit = 1 question or flashcard generated · Credits refresh monthly</p>

                {/* 3-tier cards */}
                <div className="grid grid-cols-3 gap-2 mb-5">
                    {CLINICALS_TIERS.map(tier => (
                        <div
                            key={tier.name}
                            className={`rounded-xl p-3 border text-center ${tier.popular ? 'border-emerald-400/30 bg-emerald-500/10' : 'border-white/8 bg-slate-800/40'}`}
                        >
                            {tier.popular && (
                                <p className="text-[9px] font-semibold text-emerald-400 uppercase tracking-wider mb-1">Popular</p>
                            )}
                            <p className={`text-xs font-semibold ${tier.color === 'emerald' ? 'text-emerald-300' : tier.color === 'sky' ? 'text-sky-300' : 'text-teal-300'}`}>{tier.name}</p>
                            <p className="text-base font-bold text-white mt-0.5">{tier.price}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{tier.credits}</p>
                            {tier.highlight && (
                                <p className={`text-[9px] font-semibold mt-1.5 ${tier.color === 'emerald' ? 'text-emerald-400' : tier.color === 'sky' ? 'text-sky-400' : 'text-teal-400'}`}>{tier.highlight}</p>
                            )}
                        </div>
                    ))}
                </div>

                {/* CTAs */}
                <div className="flex flex-col gap-3">
                    <button
                        onClick={onUpgrade}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-teal-500 to-emerald-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-teal-900/40 hover:opacity-95 transition"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        View Clinicals Plans
                    </button>

                    {isAnonymous && (
                        <button
                            onClick={onSignUp}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 px-6 py-3 text-sm font-medium text-slate-200 hover:border-white/20 hover:text-white hover:bg-white/5 transition"
                        >
                            Create free account — save your progress
                        </button>
                    )}
                </div>

                <p className="mt-4 text-center text-[11px] text-slate-600">
                    Used by medical students, nursing students, NPs, PAs, and residents preparing for licensure and board exams.
                </p>
            </div>
        </div>
    </div>
);

export default ClinicalGateModal;
